// GRADE — cor x tamanho por canal (ADR-012 F1) + redesign W5 (plano secao 5 W5).
// Desenho: docs/specs/2026-09-21-produto-variavel-fotos-por-cor-grade-por-canal-design.md §7
//
// Antes (pre-redesign) a aba "Grade" entregava a matriz 45x6 como PRIMEIRO
// CONTATO — um dos 2 problemas estruturais que o redesign corrige (plano
// 0.0). Agora: `useGridData` centraliza fetch+mutation (identico a antes),
// `SizeCardsProduct` mostra 6 cartoes de tamanho no nivel do produto (Base),
// `SizeCardsColor` mostra 6 cartoes no nivel da cor selecionada, e a matriz
// 45x6 vira `SizeMatrixModal`, so aberta sob clique ("Ver matriz cor x tamanho").
// O componente NAO recalcula nada — so mostra o motivo que veio pronto do
// backend e chama o MESMO endpoint de variation-rules de hoje.
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import type { GridResponse, ProductDetail, VariationRuleKind } from "../lib/types";
import { EmptyState } from "./ui/empty-state";
import { Skeleton } from "./ui/skeleton";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { cn } from "../lib/utils";
import { ColorSwatch } from "./product/ColorSwatch";

interface ToggleArgs {
  kind: VariationRuleKind;
  value: string;
  enabled: boolean;
}

// Hook compartilhado por SizeCardsProduct/SizeCardsColor (mesmo par no mesmo
// arquivo, como o antigo GridTab.tsx ja fazia com AxisSwitch/LegendDot).
// eslint-disable-next-line react-refresh/only-export-components
export function useGridData(product: ProductDetail, slug: string) {
  const qc = useQueryClient();
  const queryKey = ["product-grid", slug, product.sku];

  const query = useQuery<GridResponse>({
    queryKey,
    queryFn: async () => {
      const r = await api.get<GridResponse>(`/api/channels/${slug}/products/${product.sku}/grid`);
      return r.data;
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async (p: ToggleArgs) => {
      await api.put(`/api/channels/${slug}/products/${product.sku}/variation-rules`, {
        kind: p.kind,
        value: p.value,
        scope: "product",
        enabled: p.enabled,
      });
    },
    onSuccess: (_d, p) => {
      toast.success(
        p.enabled
          ? "Volta a aparecer na loja na próxima republicação."
          : "Ficará oculto na loja na próxima republicação.",
      );
      void qc.invalidateQueries({ queryKey });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  /** Ligar/desligar em lote (espinha de cores, W5) — mesmo endpoint /batch da W0. */
  const batchMutation = useMutation({
    mutationFn: async (rules: { kind: VariationRuleKind; value: string; enabled: boolean }[]) => {
      const r = await api.put<{ ok: boolean; applied: number }>(
        `/api/channels/${slug}/products/${product.sku}/variation-rules/batch`,
        { rules: rules.map((r2) => ({ ...r2, scope: "product" })) },
      );
      return r.data;
    },
    onSuccess: (d) => {
      toast.success(`${d.applied} regra${d.applied !== 1 ? "s" : ""} aplicada${d.applied !== 1 ? "s" : ""}.`);
      void qc.invalidateQueries({ queryKey });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const cellMap = useMemo(() => {
    const m = new Map<string, GridResponse["cells"][number]>();
    for (const c of query.data?.cells ?? []) m.set(`${c.color}|${c.size}`, c);
    return m;
  }, [query.data]);

  return { ...query, toggleMutation, batchMutation, cellMap, queryKey };
}

export type GridManager = ReturnType<typeof useGridData>;

// Funcao pura (NAO e componente — chamada direto, nunca como <GridEmptyOrError/>):
// um componente sempre devolve um elemento truthy mesmo quando renderiza null por
// dentro, entao `if (<GridEmptyOrError/>)` nunca seria falso e SizeCardsProduct/
// SizeCardsColor ficariam SEMPRE presos no estado vazio (bug medido no smoke W5:
// "Tamanhos no canal"/"Tamanhos de <cor>" sumiam da tela com dado carregado).
function gridEmptyOrError(mgr: GridManager): JSX.Element | null {
  if (mgr.isLoading) {
    return (
      <div className="stack">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (mgr.isError) {
    const status = (mgr.error as { response?: { status?: number } } | undefined)?.response?.status;
    if (status === 404) {
      return (
        <EmptyState
          icon={<LayoutGrid className="h-[3em] w-[3em]" />}
          title="Grade ainda não disponível"
          description="Esta seção depende de uma parte do sistema que está sendo publicada agora. Tente de novo em alguns minutos."
        />
      );
    }
    return (
      <EmptyState
        icon={<AlertTriangle className="h-[3em] w-[3em]" />}
        title="Não foi possível carregar a grade"
        description={apiErrorMessage(mgr.error)}
      />
    );
  }
  if (!mgr.data || mgr.data.colors.length === 0 || mgr.data.sizes.length === 0) {
    return (
      <EmptyState
        title="Sem cor ou tamanho no Sisplan"
        description="Este produto ainda não tem variações ativas no Sisplan para montar a grade."
      />
    );
  }
  return null;
}

/* ============================================================
 * TAMANHOS NO CANAL (Base do produto) — 6 cartoes + botao da matriz
 * ============================================================ */
export function SizeCardsProduct({
  mgr,
  disabled,
}: {
  mgr: GridManager;
  disabled: boolean;
}): JSX.Element {
  const [matrixOpen, setMatrixOpen] = useState(false);
  const fallback = gridEmptyOrError(mgr);
  if (fallback) return <div className="card">{fallback}</div>;

  const { sizes, colors, totals } = mgr.data!;

  return (
    <div className="card">
      <div className="spread">
        <div style={{ fontSize: "var(--text-xl)", fontWeight: 900 }}>Tamanhos no canal</div>
        <span className="card__meta">desligar aqui vale para todas as cores</span>
        <Button type="button" variant="secondary" onClick={() => setMatrixOpen(true)}>
          Ver matriz cor × tamanho
        </Button>
      </div>

      <div className="cards" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(6rem, 1fr))", marginTop: ".75rem" }}>
        {sizes.map((s) => {
          const colorsForSize = colors.filter((c) => mgr.cellMap.get(`${c.value}|${s.value}`)?.hidden_by == null).length;
          return (
            <div key={s.value} className="card" style={{ padding: ".625rem" }}>
              <div className="spread">
                <span style={{ fontSize: "var(--text-lg)", fontWeight: 900 }}>{s.value}</span>
                <AxisSwitch
                  label={`Tamanho ${s.value}`}
                  hiddenBy={s.hidden_by}
                  disabled={disabled}
                  pending={mgr.toggleMutation.isPending}
                  onToggle={() =>
                    mgr.toggleMutation.mutate({ kind: "size", value: s.value, enabled: s.hidden_by === "product" })
                  }
                />
              </div>
              <div className="mono" style={{ fontSize: "var(--text-base)", color: "var(--color-ink-2)" }}>
                {colorsForSize} cores
              </div>
            </div>
          );
        })}
      </div>

      <div className="spread" style={{ marginTop: ".75rem", fontSize: "var(--text-sm)", color: "var(--color-ink-2)" }}>
        <span>
          <b style={{ color: "var(--color-ink)" }}>{totals.enabled} de {totals.total}</b> combinações ligadas neste canal.
        </span>
      </div>

      <Dialog open={matrixOpen} onOpenChange={setMatrixOpen}>
        <DialogContent style={{ maxWidth: "min(64rem, 92vw)" }}>
          <DialogHeader>
            <DialogTitle>Matriz cor × tamanho</DialogTitle>
          </DialogHeader>
          <SizeMatrix mgr={mgr} disabled={disabled} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================================================
 * TAMANHOS DA COR (Cor selecionada) — 6 cartoes com quantidade real
 * ============================================================ */
export function SizeCardsColor({
  color,
  mgr,
  disabled,
  onApplyToSelected,
  onDisableColor,
  canApplyToSelected,
}: {
  color: string;
  mgr: GridManager;
  disabled: boolean;
  onApplyToSelected: (sizes: string[]) => void;
  onDisableColor: () => void;
  canApplyToSelected: boolean;
}): JSX.Element {
  const fallback = gridEmptyOrError(mgr);
  if (fallback) return <div className="card">{fallback}</div>;

  const { sizes } = mgr.data!;
  const colorRow = mgr.data!.colors.find((c) => c.value === color) ?? null;
  const rowDisabled = colorRow?.hidden_by != null;

  const enabledSizes = sizes
    .map((s) => mgr.cellMap.get(`${color}|${s.value}`))
    .filter((c) => c && c.hidden_by === null)
    .map((c) => c!.size);

  return (
    <div className="card">
      <div className="spread">
        <div style={{ fontSize: "var(--text-xl)", fontWeight: 900 }}>Tamanhos de {color}</div>
        <span className="card__meta">quantidade vem do Sisplan · estoque 0 fica "esgotado" na loja</span>
      </div>
      <div className="cards" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(5.5rem, 1fr))", marginTop: ".75rem" }}>
        {sizes.map((s) => {
          const cell = mgr.cellMap.get(`${color}|${s.value}`);
          if (!cell) {
            return (
              <div key={s.value} className="card" style={{ padding: ".625rem", textAlign: "center", opacity: 0.5 }}>
                <span style={{ fontWeight: 900 }}>{s.value}</span>
                <div className="mono">—</div>
              </div>
            );
          }
          const effectivelyOff = rowDisabled || s.hidden_by !== null || cell.hidden_by !== null;
          const inheritedOff = rowDisabled || s.hidden_by !== null;
          return (
            <div key={s.value} className="card" style={{ padding: ".625rem", display: "flex", flexDirection: "column", alignItems: "center", gap: ".375rem", opacity: effectivelyOff ? 0.6 : 1 }}>
              <span style={{ fontSize: "var(--text-lg)", fontWeight: 900 }}>{s.value}</span>
              <span className="mono" style={{ fontSize: "1.125rem", fontWeight: 900 }}>{cell.stock}</span>
              <button
                type="button"
                role="switch"
                aria-checked={cell.hidden_by === null}
                aria-label={`${color} ${s.value}`}
                disabled={disabled || inheritedOff || mgr.toggleMutation.isPending}
                onClick={() => mgr.toggleMutation.mutate({ kind: "combo", value: cell.sku_variation, enabled: cell.hidden_by === "combo" })}
                className="switch"
                style={{ background: cell.hidden_by === null ? "var(--color-good)" : "var(--color-line)" }}
              />
            </div>
          );
        })}
      </div>
      <div className="cluster" style={{ marginTop: ".75rem" }}>
        <Button
          type="button"
          variant="secondary"
          disabled={disabled || !canApplyToSelected || enabledSizes.length === 0}
          title={!canApplyToSelected ? "Selecione outras cores na espinha para aplicar" : undefined}
          onClick={() => onApplyToSelected(enabledSizes)}
        >
          Aplicar estes tamanhos às cores selecionadas
        </Button>
        <Button type="button" variant="destructive" disabled={disabled} onClick={onDisableColor}>
          Desligar esta cor no canal
        </Button>
      </div>
    </div>
  );
}

function SizeMatrix({ mgr, disabled }: { mgr: GridManager; disabled: boolean }): JSX.Element {
  const { colors, sizes, cells } = mgr.data!;
  const conflictCount = cells.filter((c) => c.conflict).length;

  return (
    <div className="stack">
      <div className="cluster" style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-2)" }}>
        <LegendDot tone="good" label="ligada" />
        <LegendDot tone="neutral" label="desligada neste canal" />
        <LegendDot tone="warn" label="travada por regra do canal inteiro" />
        {conflictCount > 0 ? (
          <span style={{ color: "var(--color-bad)", fontWeight: 700 }}>
            ● {conflictCount} conflito{conflictCount > 1 ? "s" : ""} no Sisplan
          </span>
        ) : null}
      </div>

      <div style={{ overflowX: "auto", border: ".0625rem solid var(--color-line)", borderRadius: "var(--radius-md)", maxHeight: "24rem", overflowY: "auto" }}>
        <table style={{ borderSpacing: 0, width: "100%", minWidth: `${11 + sizes.length * 4.2}em` }}>
          <thead>
            <tr>
              <th
                style={{ position: "sticky", left: 0, top: 0, zIndex: 20, background: "var(--color-soft)", borderBottom: ".0625rem solid var(--color-line)", padding: ".375rem .5rem", textAlign: "left", fontSize: "var(--text-sm)", fontWeight: 800, minWidth: "11em" }}
              >
                Cor ╲ Tamanho
              </th>
              {sizes.map((s) => (
                <th key={s.value} style={{ position: "sticky", top: 0, zIndex: 10, background: "var(--color-soft)", borderBottom: ".0625rem solid var(--color-line)", padding: ".375rem", textAlign: "center", fontSize: "var(--text-sm)", fontWeight: 800 }}>
                  {s.value}
                  {s.hidden_by === "channel" ? (
                    <span className="chip chip--warn" style={{ marginLeft: ".25rem" }}>todo o canal</span>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {colors.map((color) => {
              const rowDisabled = color.hidden_by !== null;
              return (
                <tr key={color.value}>
                  <td style={{ position: "sticky", left: 0, zIndex: 10, background: "var(--color-card)", borderBottom: ".0625rem solid var(--color-line)", padding: ".375rem .5rem" }}>
                    <div className="cluster" style={{ gap: ".5rem" }}>
                      <ColorSwatch name={color.value} size=".875rem" />
                      <span className={cn(rowDisabled ? "text-ink-2 line-through" : undefined)}>{color.value}</span>
                      {color.hidden_by === "channel" ? <span className="chip chip--warn">todo o canal</span> : null}
                    </div>
                  </td>
                  {sizes.map((size) => {
                    const cell = mgr.cellMap.get(`${color.value}|${size.value}`);
                    if (!cell) {
                      return <td key={size.value} style={{ borderBottom: ".0625rem solid var(--color-line)", padding: ".375rem", textAlign: "center", color: "var(--color-ink-3)" }}>—</td>;
                    }
                    const effectivelyOff = rowDisabled || size.hidden_by !== null || cell.hidden_by !== null;
                    const inheritedOff = rowDisabled || size.hidden_by !== null;
                    return (
                      <td key={size.value} style={{ borderBottom: ".0625rem solid var(--color-line)", padding: ".375rem", textAlign: "center", background: effectivelyOff ? "var(--color-soft)" : undefined }}>
                        <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: ".25rem", minWidth: "3.4em" }}>
                          <span className="mono" style={{ fontWeight: 700, textDecoration: effectivelyOff ? "line-through" : undefined, color: effectivelyOff ? "var(--color-ink-2)" : "var(--color-ink)" }}>
                            {cell.stock}
                          </span>
                          {cell.conflict ? (
                            <span style={{ fontSize: "var(--text-micro)", fontWeight: 700, color: "var(--color-bad)" }} title={`Sisplan tem mais de uma variação; a vitrine publica a de menor código (${cell.conflict_with.join(", ")})`}>
                              ● conflito
                            </span>
                          ) : null}
                          <button
                            type="button"
                            role="switch"
                            aria-checked={cell.hidden_by === null}
                            aria-label={`${color.value} ${size.value}`}
                            disabled={disabled || inheritedOff || mgr.toggleMutation.isPending}
                            onClick={() => mgr.toggleMutation.mutate({ kind: "combo", value: cell.sku_variation, enabled: cell.hidden_by === "combo" })}
                            className="switch"
                            style={{ background: cell.hidden_by === null ? "var(--color-good)" : "var(--color-line)" }}
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LegendDot({ tone, label }: { tone: "good" | "neutral" | "warn"; label: string }): JSX.Element {
  const bg = tone === "good" ? "var(--color-good-soft)" : tone === "warn" ? "var(--color-warn-soft)" : "var(--color-soft)";
  return (
    <span className="cluster" style={{ gap: ".375rem" }}>
      <span style={{ display: "inline-block", height: "1em", width: "1em", borderRadius: "var(--radius-sm)", border: ".0625rem solid var(--color-line)", background: bg }} aria-hidden />
      {label}
    </span>
  );
}

function AxisSwitch({
  label,
  hiddenBy,
  disabled,
  pending,
  onToggle,
}: {
  label: string;
  hiddenBy: "product" | "channel" | null;
  disabled: boolean;
  pending: boolean;
  onToggle: () => void;
}): JSX.Element {
  const lockedByChannel = hiddenBy === "channel";
  const checked = hiddenBy === null;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={
        lockedByChannel
          ? `${label} — oculto em todo o canal, fale com o admin`
          : checked
            ? `${label} — ligado, clique para ocultar`
            : `${label} — oculto, clique para religar`
      }
      title={lockedByChannel ? "Oculto em todo o canal (regra do admin)" : undefined}
      disabled={disabled || lockedByChannel || pending}
      onClick={onToggle}
      className="switch"
      style={{ background: checked ? "var(--color-good)" : lockedByChannel ? "var(--color-warn)" : "var(--color-line)" }}
    />
  );
}
